import { Button, useDisclosure } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { IntegrationTestingConfigurationForm } from "features/internal/integration-testing/tabs/InternalIntegrationTestingConfigurationForm";
import {
  getInternalIntegrationTestingConfigurationTableRetrieveQueryKey,
  useInternalIntegrationTestingConfigurationTableRetrieveSuspense,
} from "gen/endpoints/integration-testing-configuration-table/integration-testing-configuration-table";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { rootRoute } from "shared/navigation/Routes";

export const InternalIntegrationTestingConfigurationTab: React.FC = () => {
  const { t } = useTranslation(["internal", "common"]);

  const queryClient = useQueryClient();

  const {
    isOpen: isAddConfigurationModalOpen,
    onOpen: openAddConfigurationModal,
    onClose: closeConfigurationModal,
  } = useDisclosure();

  const { data: configurationTableData } =
    useInternalIntegrationTestingConfigurationTableRetrieveSuspense();

  const onSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey:
        getInternalIntegrationTestingConfigurationTableRetrieveQueryKey(),
    });
    closeConfigurationModal();
  };

  const getLinkInfoForConfigurationRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      if (metadata?.type === "id_row") {
        return {
          type: "generic",
          to: rootRoute({})
            .internal({})
            .integrationTesting({})
            .configuration({ integrationTestingConfigurationId: metadata.id })
            .$,
        };
      }
      return undefined;
    },
    [],
  );

  return (
    <PageTabBody>
      <IntegrationTestingConfigurationForm
        isOpen={isAddConfigurationModalOpen}
        onSuccess={onSuccess}
        onClose={closeConfigurationModal}
      />
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("internal:integrationTesting.configuration.title")}
          </TableBoxTitle>
          <DataTableBoxActions>
            <Button
              colorScheme="primary"
              size="sm"
              onClick={openAddConfigurationModal}
            >
              {t("internal:integrationTesting.configuration.add.title")}
            </Button>
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={configurationTableData}
            getLinkInfoForRow={getLinkInfoForConfigurationRow}
            emptyStateProps={{
              title: t(
                "internal:integrationTesting.configuration.emptyState.title",
              ),
              description: t(
                "internal:integrationTesting.configuration.emptyState.description",
              ),
            }}
            showAllRows={true}
          />
        </TableBoxBody>
      </TableBox>
    </PageTabBody>
  );
};
